import {
  BoardTimePicker,
  RankInput,
  StartTimePicker
} from '@library/components/simulations';
import {
  PlayerImage,
  PlayerName,
  PlayerProfile,
  PlayerSecondaryHeader,
  PlayerSubheader
} from '@library/components/player';
import { classNames, isStatusRemoved } from '@library/utils';
import { ActiveSwitch } from '@library/components/modals/PlayerModal/NoContextPlayerModal/StatTable';
import { DateTime } from 'luxon';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import { ProjectionInput } from '@library/components/inputs';
import { formatWithTimezoneChange } from '@library/utils/dateTime';

export interface ProjectionRowProps {
  existingProjection?: GamePlayerProjectionTrading;
  onChange: ({
    change,
    initialProjection
  }: {
    change: GamePlayerProjectionTrading;
    initialProjection: GamePlayerProjectionTrading;
  }) => void;
  initialProjection: GamePlayerProjectionTrading;
  projectionChanges?: Partial<GamePlayerProjectionTrading>;
}

export const ProjectionRow = ({
  existingProjection = undefined,
  onChange,
  initialProjection,
  projectionChanges = {}
}: ProjectionRowProps) => {
  const displayProjection: GamePlayerProjectionTrading = {
    ...initialProjection,
    ...projectionChanges
  };

  const {
    boardTime,
    description,
    foreignId,
    isPromo,
    oddsType,
    optIn = false,
    playerImage,
    playerName,
    projectionId,
    projectionValue,
    rank,
    simsProbabilityOfMore,
    startTime,
    statType,
    status
  } = displayProjection;

  const handleChange = ({
    key,
    value
  }: {
    key: keyof GamePlayerProjectionTrading;
    value: boolean | number | string | undefined;
  }) => {
    const granularChange = { [key]: value };

    const changedProjection: GamePlayerProjectionTrading = {
      ...displayProjection,
      ...granularChange
    };

    onChange({
      change: changedProjection,
      initialProjection: displayProjection
    });
  };

  const handleChangeTime = ({
    key,
    value
  }: {
    key: 'startTime' | 'boardTime';
    value: DateTime;
  }) => {
    handleChange({
      key,
      value: value.toISO() ?? ''
    });
  };

  const startDateTime = DateTime.fromISO(startTime);
  const startDateTimeDisplayValue = formatWithTimezoneChange(
    startDateTime,
    'ccc t ZZZZ'
  );
  const playerDescription = `vs. ${description}`;
  const isRemoved = isStatusRemoved(status);

  const hasExistingProjection = existingProjection !== undefined;

  return (
    <>
      <div className="border-soFresh-140 col-span-6 h-px w-[calc(100%-2rem)] border-b" />
      <PlayerProfile
        className="flex h-full shrink-0 basis-52 items-center gap-4 place-self-start self-stretch pl-4"
        name={playerName}
        imageUrl={playerImage}
        secondaryHeader={startDateTimeDisplayValue}
        subheader={playerDescription}
      >
        <PlayerImage className="bg-soFresh-120 size-12 rounded-lg" />
        <div className="flex flex-col">
          <PlayerName className="font-heebo text-soClean-100 block text-sm font-medium leading-tight tracking-tight" />
          <PlayerSubheader className="text-soClean-180 font-heebo block text-xs tracking-tight" />
          <PlayerSecondaryHeader className="text-soClean-180 font-heebo block text-xs tracking-tight" />
        </div>
      </PlayerProfile>

      <ProjectionInput
        className={classNames(
          'focus:border-msJackson rounded-md disabled:cursor-not-allowed',
          initialProjection.projectionValue !== projectionValue
            ? 'border-msJackson'
            : 'border-soFresh-140'
        )}
        id={`${projectionId}-player-modal-projectionValue`}
        isExistingProjection={hasExistingProjection}
        isPromo={isPromo}
        oddsType={oddsType}
        optIn={optIn}
        onValueChange={(value?: string) =>
          handleChange({
            key: 'projectionValue',
            value: value ? Number(value) : ''
          })
        }
        simsProbabilityOfMore={simsProbabilityOfMore}
        value={projectionValue}
      />

      <BoardTimePicker
        boardTime={boardTime}
        handleChange={(value) => handleChangeTime({ key: 'boardTime', value })}
        initialBoardTime={initialProjection.boardTime}
        startTime={startTime}
        statusIndicator
      />
      <StartTimePicker
        handleChange={(value) => handleChangeTime({ key: 'startTime', value })}
        initialStartTime={initialProjection.startTime}
        startTime={startTime}
      />
      <RankInput
        handleProjectionRankChange={(value) =>
          handleChange({ key: 'rank', value: (value ?? '').toString() })
        }
        initialRank={initialProjection.rank}
        projectionId={projectionId}
        rank={rank}
      />
      <div className="place-self-end pr-4">
        {hasExistingProjection && (
          <span className="text-soClean-100">Existing Projection</span>
        )}
        <ActiveSwitch
          active={!isRemoved}
          disabled={false}
          handleChange={(active: boolean) =>
            handleChange({
              key: 'status',
              value: active ? 'pre_game' : 'removed'
            })
          }
          isTableElement={false}
          statType={`${foreignId}-${statType}`}
        />
      </div>
    </>
  );
};
